/**
 * Internationalisation.
 *
 * Specification: ../../test/i18n.spec.ts. Master plan §3 K10.
 *
 * archive/v0 had none, and its strings were inconsistently bilingual. This is
 * built from the start because retrofitting it means touching every component
 * in the estate.
 *
 * Three things make it more than a lookup table:
 *
 *   - CZECH PLURALS HAVE FOUR CATEGORIES and one of them is decimals:
 *     1 položka · 2–4 položky · 5+ položek · 1,5 položky. An English-shaped
 *     `count === 1 ? a : b` is wrong for most numbers a POS displays.
 *   - THE WORKING LANGUAGES OF A CZECH KITCHEN ARE NOT ONLY CZECH. Ukrainian
 *     and Slovak are daily languages for a large share of restaurant staff,
 *     and a POS in a line cook's own language is a cheap differentiator no
 *     local competitor offers. Ukrainian's plural rule is its own.
 *   - A MISSING TRANSLATION MUST NEVER REACH A GUEST AS A KEY, and `t()` must
 *     never throw — a POS that crashes on a malformed message string is a POS
 *     that has stopped taking money.
 *
 * Plural categories come from `Intl.PluralRules` rather than a hand-written
 * table: CLDR is the authority, it is already in the runtime, and hand-rolled
 * Slavic plural rules are wrong in ways only native speakers notice.
 *
 * The message syntax is the ICU subset a POS actually needs — arguments,
 * plural, select, number — rather than a dependency. Bundle size is a shipping
 * budget on the till (§3 K8).
 */

import { formatMoney, type Money } from "../money";

export interface Catalog {
  readonly locale: string;
  readonly messages: Readonly<Record<string, string>>;
}

export type MessageArgs = Readonly<Record<string, string | number | bigint>>;

export interface TranslatorOptions {
  readonly locale: string;
  /** Used when the active locale lacks a key. */
  readonly fallbackLocale: string;
  /** Called on every fallback, so gaps are fixable rather than invisible. */
  readonly onMissing?: (key: string, locale: string) => void;
}

// --- Message parsing -------------------------------------------------------

type Node =
  | { readonly t: "lit"; readonly v: string }
  | { readonly t: "hash" }
  | { readonly t: "arg"; readonly name: string }
  | { readonly t: "number"; readonly name: string }
  | {
      readonly t: "plural" | "select";
      readonly name: string;
      readonly options: ReadonlyMap<string, readonly Node[]>;
    };

/**
 * Tolerant recursive-descent parse of the ICU subset.
 *
 * Tolerant is the operative word: every malformed-input path returns what it
 * has rather than throwing. A message somebody mistyped in a translation tool
 * must degrade to imperfect text, never to a blank screen mid-service.
 */
function parse(input: string, from = 0, nested = false): { nodes: Node[]; end: number } {
  const nodes: Node[] = [];
  let literal = "";
  let i = from;

  const flush = (): void => {
    if (literal) {
      nodes.push({ t: "lit", v: literal });
      literal = "";
    }
  };

  while (i < input.length) {
    const ch = input[i]!;

    if (ch === "'") {
      const next = input[i + 1];
      if (next === "'") {
        literal += "'";
        i += 2;
        continue;
      }
      // ICU quoting: an apostrophe before a syntax character opens a literal
      // run that closes at the next apostrophe.
      if (next === "{" || next === "}" || next === "#") {
        const close = input.indexOf("'", i + 2);
        if (close === -1) {
          literal += input.slice(i + 1);
          i = input.length;
        } else {
          literal += input.slice(i + 1, close);
          i = close + 1;
        }
        continue;
      }
      literal += "'";
      i++;
      continue;
    }

    if (ch === "#" && nested) {
      flush();
      nodes.push({ t: "hash" });
      i++;
      continue;
    }

    if (ch === "}" && nested) {
      flush();
      return { nodes, end: i };
    }

    if (ch === "{") {
      flush();
      const parsed = parseArg(input, i + 1);
      nodes.push(parsed.node);
      i = parsed.end;
      continue;
    }

    literal += ch;
    i++;
  }

  flush();
  return { nodes, end: i };
}

const skipSpace = (input: string, i: number): number => {
  while (i < input.length && /\s/.test(input[i]!)) i++;
  return i;
};

function parseArg(input: string, from: number): { node: Node; end: number } {
  let i = skipSpace(input, from);
  const nameStart = i;
  while (i < input.length && !/[,}\s]/.test(input[i]!)) i++;
  const name = input.slice(nameStart, i);

  i = skipSpace(input, i);
  if (input[i] === "}") return { node: { t: "arg", name }, end: i + 1 };
  if (input[i] !== ",") return { node: { t: "arg", name }, end: i };

  i = skipSpace(input, i + 1);
  const kindStart = i;
  while (i < input.length && !/[,}\s]/.test(input[i]!)) i++;
  const kind = input.slice(kindStart, i);
  i = skipSpace(input, i);

  if (kind === "number") {
    // Skip any style; a POS needs the locale's default grouping and nothing more.
    while (i < input.length && input[i] !== "}") i++;
    return { node: { t: "number", name }, end: i + 1 };
  }

  if (kind !== "plural" && kind !== "select" && kind !== "selectordinal") {
    while (i < input.length && input[i] !== "}") i++;
    return { node: { t: "arg", name }, end: i + 1 };
  }

  if (input[i] === ",") i = skipSpace(input, i + 1);

  const options = new Map<string, readonly Node[]>();
  while (i < input.length && input[i] !== "}") {
    const keyStart = i;
    while (i < input.length && !/[{\s]/.test(input[i]!)) i++;
    const key = input.slice(keyStart, i).replace(/^=/, "");
    i = skipSpace(input, i);

    if (input[i] !== "{") break;
    const body = parse(input, i + 1, true);
    options.set(key, body.nodes);
    i = body.end + 1;
    i = skipSpace(input, i);
  }

  return {
    node: { t: kind === "select" ? "select" : "plural", name, options },
    end: i + 1,
  };
}

// --- Rendering -------------------------------------------------------------

function render(
  nodes: readonly Node[],
  args: MessageArgs,
  locale: string,
  plurals: Intl.PluralRules,
  numbers: Intl.NumberFormat,
  hashValue?: number,
): string {
  let out = "";

  for (const node of nodes) {
    switch (node.t) {
      case "lit":
        out += node.v;
        break;

      case "hash":
        out += hashValue === undefined ? "" : numbers.format(hashValue);
        break;

      case "arg": {
        const value = args[node.name];
        // A missing argument renders as its placeholder rather than as
        // nothing. "Void ?" reads like the system lost the item, and staff
        // learn to click through it; "Void {item}?" reads like a bug and gets
        // reported.
        out += value === undefined ? `{${node.name}}` : String(value);
        break;
      }

      case "number": {
        const value = args[node.name];
        out += value === undefined ? `{${node.name}}` : numbers.format(Number(value));
        break;
      }

      case "select": {
        const value = args[node.name];
        const chosen = options(node.options, String(value ?? ""), "other");
        out += render(chosen, args, locale, plurals, numbers, hashValue);
        break;
      }

      case "plural": {
        const raw = args[node.name];
        const count = raw === undefined ? 0 : Number(raw);
        // Exact matches (`=0`, `=1`) win over the CLDR category, which is what
        // lets a message say "no items" rather than "0 items".
        const exact = node.options.get(String(count));
        const chosen = exact ?? options(node.options, plurals.select(count), "other");
        out += render(chosen, args, locale, plurals, numbers, count);
        break;
      }
    }
  }

  return out;
}

const options = (
  map: ReadonlyMap<string, readonly Node[]>,
  key: string,
  fallback: string,
): readonly Node[] => map.get(key) ?? map.get(fallback) ?? [];

// --- Translator ------------------------------------------------------------

export class Translator {
  readonly #catalogs: readonly Catalog[];
  readonly #fallbackLocale: string;
  readonly #onMissing: ((key: string, locale: string) => void) | undefined;
  readonly #parsed = new Map<string, readonly Node[]>();

  #locale: string;
  #plurals: Intl.PluralRules;
  #numbers: Intl.NumberFormat;

  constructor(catalogs: readonly Catalog[], options: TranslatorOptions) {
    this.#catalogs = catalogs;
    this.#locale = options.locale;
    this.#fallbackLocale = options.fallbackLocale;
    this.#onMissing = options.onMissing;
    this.#plurals = new Intl.PluralRules(this.#locale);
    this.#numbers = new Intl.NumberFormat(this.#locale);
  }

  get locale(): string {
    return this.#locale;
  }

  /**
   * Switch language without rebuilding anything.
   *
   * A shift change from a Czech manager to a Ukrainian cook happens mid
   * service on the same device, and must not need a reload.
   */
  setLocale(locale: string): void {
    this.#locale = locale;
    this.#plurals = new Intl.PluralRules(locale);
    this.#numbers = new Intl.NumberFormat(locale);
  }

  locales(): string[] {
    return [...new Set(this.#catalogs.map((c) => c.locale))];
  }

  #messagesFor(locale: string): Readonly<Record<string, string>> | undefined {
    return this.#catalogs.find((c) => c.locale === locale)?.messages;
  }

  /**
   * Translate.
   *
   * Never throws and never returns blank. In descending order of preference:
   * the active locale, the fallback locale, then the key itself — which at
   * least tells support what to search for, where a blank button looks like a
   * rendering failure and is unusable.
   */
  t(key: string, args: MessageArgs = {}): string {
    let message = this.#messagesFor(this.#locale)?.[key];

    if (message === undefined) {
      this.#onMissing?.(key, this.#locale);
      message = this.#messagesFor(this.#fallbackLocale)?.[key];
    }
    if (message === undefined) return key;

    try {
      const cacheKey = `${this.#locale}::${key}`;
      let nodes = this.#parsed.get(cacheKey);
      if (!nodes) {
        nodes = parse(message).nodes;
        this.#parsed.set(cacheKey, nodes);
      }
      return render(nodes, args, this.#locale, this.#plurals, this.#numbers);
    } catch {
      // Belt and braces over an already-tolerant parser. A message somebody
      // mistyped in a translation tool must degrade to imperfect text, never
      // to a blank screen in the middle of service.
      return message;
    }
  }

  /** Display only. There is deliberately no inverse — see @gf/kernel/money. */
  formatMoney(amount: Money): string {
    return formatMoney(amount, this.#locale);
  }

  formatNumber(value: number): string {
    return this.#numbers.format(value);
  }

  /**
   * A time, in the VENUE's zone.
   *
   * The zone is a required argument on purpose. A tablet whose timezone was
   * never set — or was set by whoever last borrowed it — must not put a sale
   * on the wrong business day on the receipt.
   */
  formatTime(instant: Date, timeZone: string): string {
    return new Intl.DateTimeFormat(this.#locale, {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(instant);
  }

  formatDate(instant: Date, timeZone: string): string {
    return new Intl.DateTimeFormat(this.#locale, {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(instant);
  }

  /**
   * Keys the reference locale has and the active one does not.
   *
   * The translator's worklist, and a CI gate: shipping a Ukrainian POS that is
   * half English is worse than shipping it in English, because it reads as
   * unfinished rather than as unsupported.
   */
  missingAgainst(referenceLocale: string): string[] {
    const reference = this.#messagesFor(referenceLocale) ?? {};
    const active = this.#messagesFor(this.#locale) ?? {};
    return Object.keys(reference)
      .filter((key) => active[key] === undefined)
      .sort();
  }
}

/**
 * Pick the best available locale for a preference list.
 *
 * Browsers send regional tags — `cs-CZ`, `sk-SK`, `uk-UA`. Failing to fall
 * back to the base language is the most common i18n bug there is, and it
 * silently serves English to an entire country.
 */
export function negotiateLocale(
  requested: readonly string[],
  available: readonly string[],
  fallback: string,
): string {
  for (const tag of requested) {
    const normalised = tag.toLowerCase();
    const exact = available.find((a) => a.toLowerCase() === normalised);
    if (exact) return exact;

    const base = normalised.split("-")[0]!;
    const byLanguage = available.find((a) => a.toLowerCase().split("-")[0] === base);
    if (byLanguage) return byLanguage;
  }
  return fallback;
}
